import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
    MapPin,
    ArrowRight,
    Building2,
    CheckCircle2,
    Star,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import Seo from "../lib/Seo";
import CenterCard from "../components/CenterCard";
import Breadcrumbs from "../components/Breadcrumbs";
import { fetchCenters } from "../lib/api";
import { getComuna, SEO_COMUNAS, COMUNA_SLUGS } from "../lib/seoPages";

export default function ComunaDetail() {
    const { slug } = useParams();
    const comuna = getComuna(slug);

    const [centers, setCenters] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!comuna) return;
        const controller = new AbortController();
        let cancelled = false;
        setLoading(true);
        // Try filtering by city = comuna name (many sinadicciones centers
        // store comuna in `city` field). Fallback to Santiago region.
        fetchCenters(
            { city: comuna.name, page: 1, limit: 12 },
            { signal: controller.signal },
        )
            .then(async (data) => {
                if (cancelled) return;
                if ((data.centers || []).length > 0) {
                    setCenters(data.centers);
                    setTotal(data.total || 0);
                } else {
                    // Fallback: Santiago region
                    const fb = await fetchCenters(
                        { region: "Metropolitana", page: 1, limit: 6 },
                        { signal: controller.signal },
                    );
                    if (cancelled) return;
                    setCenters(fb.centers || []);
                    setTotal(fb.total || 0);
                }
            })
            .catch(() => {
                if (cancelled) return;
                setCenters([]);
                setTotal(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [comuna]);

    if (!comuna) return <Navigate to="/santiago" replace />;

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (comuna.faq || []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    const related = COMUNA_SLUGS.filter((s) => s !== slug)
        .slice(0, 4)
        .map((s) => SEO_COMUNAS[s]);

    return (
        <>
            <Seo
                title={comuna.metaTitle || comuna.h1}
                description={comuna.metaDescription}
                path={`/santiago/${comuna.slug}`}
                keywords={`centros de rehabilitación de drogas en ${comuna.name}, rehabilitación ${comuna.name} Santiago, tratamiento drogas ${comuna.name}`}
                jsonLd={faqJsonLd}
            />

            <section className="border-b border-slate-800 bg-slate-950">
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                    <Breadcrumbs
                        items={[
                            { name: "Inicio", path: "/" },
                            { name: "Santiago", path: "/santiago" },
                            { name: comuna.name },
                        ]}
                    />
                    <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                        <MapPin className="h-3.5 w-3.5" />
                        Comuna de Santiago · Región Metropolitana
                    </div>
                    <h1
                        data-testid="comuna-h1"
                        className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl"
                    >
                        {comuna.h1}
                    </h1>
                    <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-400 sm:text-lg">
                        {comuna.intro}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
                        <Building2 className="h-4 w-4 text-cyan-400" />
                        <span className="text-slate-200">
                            <strong>{loading ? "—" : total}</strong> centros disponibles
                        </span>
                    </div>
                </div>
            </section>

            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Contexto en {comuna.name}
                    </h2>
                    <p className="mt-4 leading-relaxed text-slate-300">
                        {comuna.contexto}
                    </p>
                </div>
            </section>

            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                        <Star className="h-3.5 w-3.5 fill-cyan-400 text-cyan-400" />
                        Centros recomendados
                    </div>
                    <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Opciones disponibles para residentes de {comuna.name}
                    </h2>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {loading
                            ? Array.from({ length: 6 }).map((_, i) => (
                                  <div
                                      key={i}
                                      className="h-80 animate-pulse rounded-xl border border-slate-800 bg-slate-900"
                                  />
                              ))
                            : centers.map((c) => <CenterCard key={c.center_id} center={c} />)}
                    </div>
                </div>
            </section>

            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Cómo elegir un centro si vives en {comuna.name}
                    </h2>
                    <p className="mt-4 leading-relaxed text-slate-300">
                        {comuna.comoElegir}
                    </p>
                    <ul className="mt-6 space-y-3">
                        {[
                            "Cercanía: muchas comunas no tienen centros propios; un buen residencial fuera de la comuna puede ser mejor opción.",
                            "Acreditación SEREMI Metropolitana y, si corresponde, convenio SENDA Previene comunal.",
                            "Visita el centro presencialmente antes de internar a un familiar.",
                            "Pide presupuesto escrito; cuidado con precios que parecen muy bajos.",
                        ].map((txt, i) => (
                            <li
                                key={i}
                                className="flex gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4"
                            >
                                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-400" />
                                <span className="text-slate-300">{txt}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                        Preguntas frecuentes
                    </div>
                    <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Lo que más nos preguntan en {comuna.name}
                    </h2>
                    <Accordion
                        type="single"
                        collapsible
                        className="mt-6 rounded-xl border border-slate-800 bg-slate-900"
                        data-testid="comuna-faq"
                    >
                        {(comuna.faq || []).map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`co-faq-${i}`}
                                className="border-slate-800 px-5"
                            >
                                <AccordionTrigger className="text-left text-slate-100 hover:text-cyan-400">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-400">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            <section className="py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Otras comunas de Santiago
                    </h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={`/santiago/${r.slug}`}
                                className="card-hover rounded-xl border border-slate-800 bg-slate-900 p-5"
                            >
                                <div className="flex items-center gap-2 text-cyan-400">
                                    <MapPin className="h-4 w-4" />
                                    <span className="text-xs font-medium uppercase tracking-wider">
                                        Comuna
                                    </span>
                                </div>
                                <h3 className="mt-3 text-lg font-semibold text-slate-50">
                                    {r.name}
                                </h3>
                            </Link>
                        ))}
                        <Link
                            to="/santiago"
                            className="card-hover flex items-center justify-center rounded-xl border border-dashed border-slate-700 bg-slate-900 p-5 text-sm text-cyan-400 hover:text-cyan-300"
                        >
                            Ver todas las comunas <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
